/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-explicit-any */

import { useState, useRef, useCallback, useEffect } from "react";
import { useAppStore } from "./use-app-store";
import { useVadStore } from "@/stores/vad-store";

const NO_SPEECH_TIMEOUT_MS = 10000; // 10 seconds timeout for no speech detection

// VAD Configuration
const VAD_CONFIG = {
  silenceTimeout: 500, // 500ms of silence before stopping
  minSpeechFrames: 5,   // Minimum frames to consider as speech
  positiveSpeechThreshold: 0.6,
  negativeSpeechThreshold: 0.35,
  maxSessionDuration: 30000, // 30 seconds maximum VAD session
  frameSamples: 512,
  model: 'v5' as const
};

export function useAudioRecorder() {
  const [isRecording, setIsRecording] = useState(false);
  const [speechDetected, setSpeechDetected] = useState(false);
  const [isReady, setIsReady] = useState(false); // Track if VAD is ready to use
  
  const vadRef = useRef<any>(null); // Component's own VAD instance
  const noSpeechTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const speechEndTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const vadAudioSegmentRef = useRef<Float32Array | null>(null);
  const isVadSessionActiveRef = useRef<boolean>(false);
  
  const sendAudio = useAppStore((state) => state.sendAudio);
  const stopAudio = useAppStore((state) => state.stopAudio);
  const showQosMessage = useAppStore((state) => state.showQosMessage);
  
  // Get VAD store state and methods
  const { initializeVad, vadInitialized, isLoading: isInitializing } = useVadStore();

  // Helper function to convert Float32Array to WAV blob
  const createWavBlob = useCallback((audioData: Float32Array, sampleRate: number): Blob => {
    const length = audioData.length;
    const arrayBuffer = new ArrayBuffer(44 + length * 2);
    const view = new DataView(arrayBuffer);
    
    // WAV header
    const writeString = (offset: number, string: string) => {
      for (let i = 0; i < string.length; i++) {
        view.setUint8(offset + i, string.charCodeAt(i));
      }
    };
    
    writeString(0, 'RIFF');
    view.setUint32(4, 36 + length * 2, true);
    writeString(8, 'WAVE');
    writeString(12, 'fmt ');
    view.setUint32(16, 16, true);
    view.setUint16(20, 1, true);
    view.setUint16(22, 1, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, sampleRate * 2, true);
    view.setUint16(32, 2, true);
    view.setUint16(34, 16, true);
    writeString(36, 'data');
    view.setUint32(40, length * 2, true);
    
    // Convert float samples to 16-bit PCM
    let offset = 44;
    for (let i = 0; i < length; i++) {
      const sample = Math.max(-1, Math.min(1, audioData[i]));
      view.setInt16(offset, sample * 0x7FFF, true);
      offset += 2;
    }
    
    return new Blob([arrayBuffer], { type: 'audio/wav' });
  }, []);

  // Create component's own VAD instance
  const createComponentVadInstance = useCallback(async (retryCount = 0) => {
    if (vadRef.current || !vadInitialized) return null;

    const maxRetries = 3;
    const retryDelay = 1000; // 1 second delay between retries

    try {
      console.log(`Creating VAD instance for component... (attempt ${retryCount + 1}/${maxRetries + 1})`);

      if (typeof window.vad === 'undefined') {
        throw new Error('VAD library not available');
      }

      // Check if the wasm module is properly loaded
      if (!window.vad.MicVAD || typeof window.vad.MicVAD.new !== 'function') {
        throw new Error('VAD MicVAD not properly initialized');
      }

      const vad = await window.vad.MicVAD.new({
        positiveSpeechThreshold: VAD_CONFIG.positiveSpeechThreshold,
        negativeSpeechThreshold: VAD_CONFIG.negativeSpeechThreshold,
        redemptionMs: VAD_CONFIG.silenceTimeout,
        minSpeechMs: 250, // Minimum 250ms of speech
        model: VAD_CONFIG.model,
        
        onSpeechStart: () => {
          console.log('VAD: Speech started');
          setSpeechDetected(true);
          
          // Clear no-speech timeout since we detected speech
          if (noSpeechTimeoutRef.current) {
            clearTimeout(noSpeechTimeoutRef.current);
            noSpeechTimeoutRef.current = null;
          }
          
          // Clear any existing speech end timeout
          if (speechEndTimeoutRef.current) {
            clearTimeout(speechEndTimeoutRef.current);
            speechEndTimeoutRef.current = null;
          }
        },
        
        onSpeechEnd: (audio: Float32Array) => {
          console.log('VAD: Speech ended, audio length:', audio.length);
          vadAudioSegmentRef.current = audio;
          setSpeechDetected(false);
          
          // Set up automatic transcription ending after redemptionMs
          // This gives the user a chance to continue speaking if they pause briefly
          if (isVadSessionActiveRef.current) {
            console.log(`VAD: Speech ended, will auto-stop in ${VAD_CONFIG.silenceTimeout}ms if no more speech`);
            speechEndTimeoutRef.current = setTimeout(() => {
              console.log('VAD: Auto-stopping recording after silence timeout');
              if (isVadSessionActiveRef.current) {
                // Send the audio data when auto-stopping
                if (vadAudioSegmentRef.current) {
                  // Convert Float32Array to WAV blob
                  const wavBlob = createWavBlob(vadAudioSegmentRef.current, 16000);
                  sendAudio(wavBlob);
                }
                stopRecording();
              }
            }, VAD_CONFIG.silenceTimeout);
          }
        },
        
        onVADMisfire: () => {
          console.log('VAD: Misfire detected (speech too short)');
          showQosMessage('Speech was too short! Please try again.', 1500);
        }
      });
      
      vadRef.current = vad;
      setIsReady(true); // Mark as ready when instance is created
      console.log('VAD instance created successfully');
      return vad;
    } catch (error: any) {
      console.error(`Error creating VAD instance (attempt ${retryCount + 1}):`, error);

      // Retry logic for loading issues
      if (retryCount < maxRetries && (
        error.message?.includes('wasm') ||
        error.message?.includes('not properly initialized') ||
        error.message?.includes('Cannot read properties of undefined')
      )) {
        console.log(`VAD loading issue detected, retrying in ${retryDelay}ms...`);
        return new Promise(resolve => {
          setTimeout(async () => {
            const result = await createComponentVadInstance(retryCount + 1);
            resolve(result);
          }, retryDelay * (retryCount + 1)); // Exponential backoff
        });
      }

      // Final failure after retries
      if (retryCount >= maxRetries) {
        showQosMessage('Voice system failed to initialize. Please refresh the page.', 5000);
      } else {
        showQosMessage(`Voice Error: ${error.message || error}`, 3000);
      }
      return null;
    }
  }, [vadInitialized, showQosMessage, sendAudio, createWavBlob]);

  const startRecording = useCallback(async () => {
    if (isRecording || isInitializing) return;
    
    // If user clicks again while recording but no speech detected, cancel
    if (isRecording && !speechDetected) {
      console.log('User clicked again with no speech detected, cancelling recording');
      stopRecording();
      return;
    }
    
    // Ensure VAD is initialized globally
    if (!vadInitialized) {
      const success = await initializeVad();
      if (!success) {
        showQosMessage('Failed to initialize Voice Activity Detection', 3000);
        return;
      }
    }
    
    // Create component instance if needed (should already be preloaded)
    if (!vadRef.current) {
      await createComponentVadInstance();
      if (!vadRef.current) {
        showQosMessage('Failed to create VAD instance', 2000);
        return;
      }
    }
    
    console.log("Starting recording with VAD...");
    try {
      // Reset speech detection state
      setSpeechDetected(false);
      vadAudioSegmentRef.current = null;
      isVadSessionActiveRef.current = true;
      setIsRecording(true);
      stopAudio();
      
      // Start VAD
      await vadRef.current.start();
      console.log('VAD started successfully');
      
      // Set no-speech timeout
      noSpeechTimeoutRef.current = setTimeout(() => {
        console.log('No speech detected within timeout, stopping recording');
        showQosMessage('No speech detected. Please try again!', 2000);
        stopRecording();
      }, NO_SPEECH_TIMEOUT_MS);
      
    } catch (error) {
      console.error("Error starting recording:", error);
      setIsRecording(false);
      isVadSessionActiveRef.current = false;
      showQosMessage(`Recording Error: ${error}`, 3000);
    }
  }, [isRecording, isInitializing, speechDetected, vadInitialized, initializeVad, createComponentVadInstance, showQosMessage, stopAudio]);

  const stopRecording = useCallback(async () => {
    
    console.log("Stopping recording...");
    isVadSessionActiveRef.current = false;
    setIsRecording(false);
    
    // Clear timeouts
    if (noSpeechTimeoutRef.current) {
      clearTimeout(noSpeechTimeoutRef.current);
      noSpeechTimeoutRef.current = null;
    }
    if (speechEndTimeoutRef.current) {
      clearTimeout(speechEndTimeoutRef.current);
      speechEndTimeoutRef.current = null;
    }
    
    // Stop VAD
    if (vadRef.current) {
      try {
        await vadRef.current.pause();
        console.log('VAD stopped successfully');
      } catch (vadError) {
        console.warn('Error stopping VAD:', vadError);
      }
    }
    
    // Send audio if we have it and speech was detected
    if (vadAudioSegmentRef.current && speechDetected) {
      console.log('Sending VAD audio segment, length:', vadAudioSegmentRef.current.length);
      const wavBlob = createWavBlob(vadAudioSegmentRef.current, 16000);
      sendAudio(wavBlob);
    } else if (!vadAudioSegmentRef.current) {
      console.warn('No audio data captured');
      // showQosMessage('No audio data captured', 1500);
    }
    
    // Reset state
    vadAudioSegmentRef.current = null;
    setSpeechDetected(false);
  }, [speechDetected, createWavBlob, sendAudio]);

  // Initialize VAD and preload component instance on mount
  useEffect(() => {
    const preloadVad = async () => {
      // First ensure global VAD is initialized
      const success = await initializeVad();
      if (success && !vadRef.current) {
        // Then create the component instance ahead of time
        console.log('Preloading VAD instance for component...');
        await createComponentVadInstance();
      }
    };
    preloadVad();
  }, [initializeVad, createComponentVadInstance]);
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (noSpeechTimeoutRef.current) clearTimeout(noSpeechTimeoutRef.current);
      if (speechEndTimeoutRef.current) clearTimeout(speechEndTimeoutRef.current);
      
      if (vadRef.current) {
        try {
          vadRef.current.pause();
          vadRef.current.destroy?.();
        } catch (error) {
          console.warn('Error cleaning up VAD instance:', error);
        }
        vadRef.current = null;
        setIsReady(false);
      }
    };
  }, []);

  return { 
    isRecording, 
    isInitializing, 
    speechDetected, 
    vadInitialized,
    isReady, // Component VAD instance is ready
    startRecording, 
    stopRecording
  };
}
